import React, { Suspense, lazy, useMemo, useState } from "react";

import { ChevronDown, Download, Loader2 } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsContent } from "~/components/ui/tabs";
import { useToast } from "~/hooks/use-toast";
import { useCreditNoteCosts } from "~/hooks/useCreditNoteCosts";
import { useCurrentUser } from "~/hooks/useCurrentUser";
import { useFacturaCosts } from "~/hooks/useFacturaCosts";
import { useFacturasWithPaymentsOptimized } from "~/hooks/useFacturasWithPaymentsOptimized";
import {
  useInvalidateSalesRequest,
  useSalesRequest,
  useSalesRequestItems,
} from "~/hooks/useSalesRequest";
import CuentasPorCobrarTable from "~/pages/facturas/CuentasPorCobrarTable";
import FacturasFilterBar from "~/pages/facturas/FacturasFilterBar";
import FacturasStatistics from "~/pages/facturas/FacturasStatistics";
import FacturasTable from "~/pages/facturas/FacturasTable";
import {
  exportFacturasForBI,
  exportFacturasToExcel,
} from "~/services/excelExportService";
import { SalesRequestDocument } from "~/types/client";

const OrderDetailDialog = lazy(
  () => import("~/pages/purchase-orders/OrderDetailDialog")
);

const FacturasPage: React.FC = () => {
  // Always use optimized hooks
  const {
    facturas,
    loading,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refreshFacturas,
  } = useFacturasWithPaymentsOptimized(true);

  const { user } = useCurrentUser();
  const [selectedSalesRequestId, setSelectedSalesRequestId] = useState<
    string | null
  >(null);

  // Use React Query to fetch sales request data
  const {
    data: selectedOrder,
    isLoading: loadingOrder,
    error: salesRequestError,
  } = useSalesRequest(selectedSalesRequestId);

  // The sales request header carries no line items, so load them separately to
  // populate the order-detail dialog (otherwise it renders "No hay productos").
  const { data: selectedOrderItems = [] } =
    useSalesRequestItems(selectedSalesRequestId);

  const { invalidateSalesRequest } = useInvalidateSalesRequest();
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingBI, setExportingBI] = useState(false);
  // Test facturas are now automatically excluded at the data level
  const { toast } = useToast();

  // Filter facturas for sellers - ensure consistency with statistics filtering
  const filteredFacturas = useMemo(() => {
    if (user.role !== "seller" || !user.id) {
      return facturas; // Non-sellers see all facturas
    }

    // SECURITY: Sellers should only see facturas, not credit notes or debit notes
    const facturasOnly = facturas.filter((factura) => {
      if (factura.documentType !== "factura") {
        return false;
      }
      return true;
    });

    return facturasOnly;
  }, [facturas, user.role, user.id]);

  // WAC cost per sales_request for the cost/margin columns + summary cards.
  // Sellers don't get cost/margin data — skip the fetch entirely for them.
  const salesRequestIds = useMemo(
    () =>
      user.role === "seller"
        ? []
        : filteredFacturas
            .filter((f) => f.documentType === "factura" && f.salesRequestId)
            .map((f) => f.salesRequestId as string),
    [filteredFacturas, user.role]
  );
  const { data: costMap, isLoading: costMapLoading } =
    useFacturaCosts(salesRequestIds);

  // Credit-note (devolución) return cost, so the cost/margin cards can net NC
  // cost out of "Costo de Ventas" exactly like the "Excel detallado" export does.
  // Sellers don't see NCs or cost data, so skip the fetch for them.
  const creditNoteBsaleNumbers = useMemo(
    () =>
      user.role === "seller"
        ? []
        : (filteredFacturas
            .filter((f) => f.documentType === "nota_de_credito")
            .map((f) => (f as any).bsale_number?.toString())
            .filter(Boolean) as string[]),
    [filteredFacturas, user.role]
  );
  const { data: creditNoteCostMap, isLoading: creditNoteCostMapLoading } =
    useCreditNoteCosts(creditNoteBsaleNumbers);

  // Costo de Ventas (and the margin cards) derive from costMap + creditNoteCostMap,
  // which resolve after the facturas themselves. Keep the whole statistics panel in
  // its skeleton state until all three are settled so the KPIs render once at their
  // final value instead of flickering $0 → bruto → neto. Both cost queries are
  // disabled (and thus not loading) for sellers and empty result sets, so this only
  // waits when there is actually cost data in flight.
  const statisticsLoading =
    loading || costMapLoading || creditNoteCostMapLoading;

  // Create client maps for compatibility with existing components
  const clientNames = useMemo(() => {
    const namesMap: Record<string, string> = {};
    facturas.forEach((factura) => {
      if (factura.clientId && (factura as any).client_name) {
        namesMap[factura.clientId] = (factura as any).client_name;
      }
    });
    return namesMap;
  }, [facturas]);

  const clientTaxIds = useMemo(() => {
    const taxIdsMap: Record<string, string> = {};
    facturas.forEach((factura) => {
      if (factura.clientId && (factura as any).client_tax_id) {
        taxIdsMap[factura.clientId] = (factura as any).client_tax_id;
      }
    });
    return taxIdsMap;
  }, [facturas]);

  // Handle clicking on a factura to load its sales request
  const handleFacturaClick = (factura: SalesRequestDocument) => {
    if (!factura.salesRequestId) {
      toast({
        title: "Error",
        description: "Esta factura no tiene una nota de venta asociada.",
        variant: "destructive",
      });
      return;
    }

    // Set the sales request ID to trigger React Query fetch
    setSelectedSalesRequestId(factura.salesRequestId);
  };

  // Handle sales request error from React Query
  React.useEffect(() => {
    if (salesRequestError) {
      console.error("Error fetching sales request:", salesRequestError);
      toast({
        title: "Error",
        description:
          "No se pudo cargar la nota de venta asociada a esta factura.",
        variant: "destructive",
      });
    }
  }, [salesRequestError, toast]);

  // Mock functions needed for OrderDetailDialog
  const getSellerInfo = () => {
    return {
      name: "Vendedor",
      lastname: "",
      email: "",
    };
  };

  const getApproverRejecterInfo = () => {
    return {
      name: "Aprobador",
      lastname: "",
      email: "",
      role: "admin",
    };
  };

  // Handle BI Excel export
  const handleBIExport = async () => {
    if (filteredFacturas.length === 0) {
      toast({
        title: "Sin datos",
        description: "No hay facturas para exportar.",
        variant: "destructive",
      });
      return;
    }

    setExportingBI(true);
    try {
      await exportFacturasForBI(
        filteredFacturas,
        startDate,
        endDate,
        user.role || undefined,
        user.id || undefined
      );
      toast({
        title: "Exportación BI exitosa",
        description: "El archivo Excel para BI se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Error exporting BI data:", error);
      toast({
        title: "Error en la exportación BI",
        description:
          "No se pudo exportar el archivo Excel para BI. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setExportingBI(false);
    }
  };

  // Handle Excel export
  const handleExcelExport = async () => {
    if (filteredFacturas.length === 0) {
      toast({
        title: "Sin datos",
        description: "No hay facturas para exportar.",
        variant: "destructive",
      });
      return;
    }

    setExportingExcel(true);
    try {
      await exportFacturasToExcel(
        filteredFacturas,
        startDate,
        endDate,
        user.role || undefined,
        user.id || undefined
      );
      toast({
        title: "Exportación exitosa",
        description: "El archivo Excel se ha descargado correctamente.",
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast({
        title: "Error en la exportación",
        description:
          "No se pudo exportar el archivo Excel. Intente nuevamente.",
        variant: "destructive",
      });
    } finally {
      setExportingExcel(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Facturas"
        description="Gestión y consulta de facturas emitidas"
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-9"
                disabled={
                  loading ||
                  filteredFacturas.length === 0 ||
                  exportingExcel ||
                  exportingBI
                }
              >
                {exportingExcel || exportingBI ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {exportingExcel || exportingBI ? "Descargando..." : "Descargar"}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={handleExcelExport}
                disabled={
                  exportingExcel || loading || filteredFacturas.length === 0
                }
              >
                <Download className="mr-2 h-4 w-4" />
                {exportingExcel ? "Exportando..." : "Descargar Excel"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleBIExport}
                disabled={
                  exportingBI || loading || filteredFacturas.length === 0
                }
              >
                <Download className="mr-2 h-4 w-4" />
                {exportingBI ? "Exportando..." : "Descargar Excel Detallado"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <Tabs defaultValue="facturas" className="w-full">
        <TabsContent value="facturas" className="space-y-4">
          {/* Filters */}
          {loading ? (
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex flex-col gap-4">
                  <Skeleton className="h-6 w-16" />
                  <div className="xs:grid-cols-2 grid grid-cols-1 gap-3">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-10 w-32" />
                    </div>
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-12" />
                      <Skeleton className="h-10 w-32" />
                    </div>
                  </div>
                  <Skeleton className="h-10 w-full" />
                </div>
              </CardContent>
            </Card>
          ) : (
            <FacturasFilterBar
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              onRefresh={refreshFacturas}
              className="mb-4"
            />
          )}

          {/* Statistics component loaded directly */}
          <FacturasStatistics
            facturas={filteredFacturas}
            loading={statisticsLoading}
            startDate={startDate}
            endDate={endDate}
            userRole={user.role}
            currentUserId={user.id}
            costMap={costMap}
            creditNoteCostMap={creditNoteCostMap}
          />

          {/* Table of Facturas */}
          <FacturasTable
            facturas={filteredFacturas}
            loading={loading}
            clientNames={clientNames}
            clientTaxIds={clientTaxIds}
            onFacturaClick={handleFacturaClick}
            costMap={costMap}
            userRole={user.role}
          />
        </TabsContent>

        <TabsContent value="cuentas-por-cobrar" className="space-y-4">
          <CuentasPorCobrarTable />
        </TabsContent>
      </Tabs>

      {/* Order Detail Dialog (when a factura is clicked) */}
      {(selectedOrder || loadingOrder) && selectedSalesRequestId && (
        <Suspense
          fallback={
            <div className="fixed inset-0 flex items-center justify-center bg-black/50">
              <Skeleton className="h-[600px] w-[800px] rounded-lg" />
            </div>
          }
        >
          {loadingOrder ? (
            <div className="fixed inset-0 flex items-center justify-center bg-black/50">
              <Skeleton className="h-[600px] w-[800px] rounded-lg" />
            </div>
          ) : selectedOrder ? (
            <OrderDetailDialog
              selectedOrder={selectedOrder}
              setSelectedOrder={() => {
                setSelectedSalesRequestId(null);
              }}
              getSellerInfo={getSellerInfo}
              getApproverRejecterInfo={getApproverRejecterInfo}
              clientNames={clientNames}
              items={selectedOrderItems}
              onOrderStatusChange={() => {
                // Invalidate the sales request cache to get fresh data
                invalidateSalesRequest(selectedSalesRequestId);
              }}
            />
          ) : null}
        </Suspense>
      )}
    </PageContainer>
  );
};

export default FacturasPage;
